import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  formatPercent,
  formatUsd,
  isValidEvmAddress,
  riskTone,
  shortenAddress,
} from './mockAnalyze';
import { useLang } from '../../i18n/context';
import ToolboxReturnPanel from '../../components/ToolboxReturnPanel';
import './DumpDetectiveReportPage.css';

const SCORE_META = [
  ['contract', 'detectiveReport.scoreContract', 25],
  ['liquidity', 'detectiveReport.scoreLiquidity', 20],
  ['holders', 'detectiveReport.scoreHolders', 20],
  ['project', 'detectiveReport.scoreProject', 15],
  ['smartMoney', 'detectiveReport.scoreSmartMoney', 10],
  ['market', 'detectiveReport.scoreMarket', 10],
];

function valueLabel(value, t) {
  if (value === undefined || value === null) return t('detectiveReport.notConnected');
  if (typeof value === 'boolean') return value ? t('detectiveReport.yes') : t('detectiveReport.no');
  return value;
}

function SafetyGauge({ score, level }) {
  const { t } = useLang();
  const angle = Math.max(0, Math.min(100, score)) * 3.6;
  return (
    <section className={`dd-report-panel dd-gauge-card ${riskTone(level)}`}>
      <div
        className="dd-gauge"
        style={{ background: `conic-gradient(#2563eb 0deg, #f59e0b ${angle}deg, rgba(255,255,255,0.08) ${angle}deg 360deg)` }}
      >
        <div>
          <strong>{score}</strong>
          <span>{t('detectiveReport.safetyScoreOutOf100')}</span>
        </div>
      </div>
      <div>
        <span className="dd-risk-label">{t('detectiveReport.overallRating')}</span>
        <h2>{level}</h2>
        <p>{t('detectiveReport.safetyGaugeDesc')}</p>
      </div>
    </section>
  );
}

function RiskRadar({ scores }) {
  const { t } = useLang();
  const size = 280;
  const center = size / 2;
  const radius = 94;
  const points = SCORE_META.map(([key,, max], index) => {
    const value = scores[key] / max;
    const angle = -Math.PI / 2 + (Math.PI * 2 * index) / SCORE_META.length;
    return {
      x: center + Math.cos(angle) * radius * value,
      y: center + Math.sin(angle) * radius * value,
      labelX: center + Math.cos(angle) * (radius + 28),
      labelY: center + Math.sin(angle) * (radius + 28),
      label: t(SCORE_META[index][1]),
    };
  });
  const polygon = points.map((point) => `${point.x},${point.y}`).join(' ');

  return (
    <section className="dd-report-panel dd-radar-card">
      <h2>{t('detectiveReport.riskRadar')}</h2>
      <svg viewBox={`0 0 ${size} ${size}`} className="dd-radar" role="img" aria-label={t('detectiveReport.riskRadar')}>
        {[0.33, 0.66, 1].map((scale) => (
          <polygon
            key={scale}
            points={SCORE_META.map((_, index) => {
              const angle = -Math.PI / 2 + (Math.PI * 2 * index) / SCORE_META.length;
              return `${center + Math.cos(angle) * radius * scale},${center + Math.sin(angle) * radius * scale}`;
            }).join(' ')}
            className="dd-radar-ring"
          />
        ))}
        {SCORE_META.map((_, index) => {
          const angle = -Math.PI / 2 + (Math.PI * 2 * index) / SCORE_META.length;
          return (
            <line
              key={index}
              x1={center}
              y1={center}
              x2={center + Math.cos(angle) * radius}
              y2={center + Math.sin(angle) * radius}
              className="dd-radar-axis"
            />
          );
        })}
        <polygon points={polygon} className="dd-radar-shape" />
        {points.map((point) => (
          <text key={point.label} x={point.labelX} y={point.labelY} className="dd-radar-label">
            {point.label}
          </text>
        ))}
      </svg>
    </section>
  );
}

function ScoreGrid({ scores }) {
  const { t } = useLang();
  return (
    <section className="dd-score-grid">
      {SCORE_META.map(([key, labelKey, max]) => (
        <article className="dd-report-panel dd-score-card" key={key}>
          <span>{t(labelKey)}</span>
          <strong>{scores[key]} / {max}</strong>
          {(key === 'contract' || key === 'project' || key === 'smartMoney' || key === 'holders') && (
            <em>{t('detectiveReport.dataNotFullyConnected')}</em>
          )}
        </article>
      ))}
    </section>
  );
}

function DetectionDetails({ result }) {
  const { t } = useLang();
  const items = [
    [t('detectiveReport.dexscreenerQuote'), result.dataStatus?.dexscreener === 'ready' ? t('detectiveReport.connected') : t('detectiveReport.fetchFailed')],
    [t('detectiveReport.mainPoolDex'), result.token.dexName || t('detectiveReport.noData')],
    [t('detectiveReport.mainPoolAddress'), result.token.pairAddress ? shortenAddress(result.token.pairAddress) : t('detectiveReport.noData')],
    [t('detectiveReport.contractOpenSource'), valueLabel(result.security.isOpenSource, t)],
    [t('detectiveReport.suspectedHoneypot'), valueLabel(result.security.isHoneypot, t)],
    [t('detectiveReport.mintable'), valueLabel(result.security.isMintable, t)],
    [t('detectiveReport.blacklistMechanism'), valueLabel(result.security.hasBlacklist, t)],
    [t('detectiveReport.ownerNotRenounced'), valueLabel(result.security.ownerNotRenounced, t)],
    [t('detectiveReport.buySellTax'), `${result.security.buyTax ?? t('detectiveReport.notConnected')} / ${result.security.sellTax ?? t('detectiveReport.notConnected')}`],
    [t('detectiveReport.holderData'), result.holders.status === 'pending' ? t('detectiveReport.notConnected') : t('detectiveReport.connected')],
  ];

  return (
    <section className="dd-report-panel dd-details">
      <h2>{t('detectiveReport.detectionDetails')}</h2>
      <div>
        {items.map(([label, value]) => (
          <p key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </p>
        ))}
      </div>
    </section>
  );
}

function ReportState({ title, desc }) {
  const { t } = useLang();
  return (
    <main className="dd-report-page">
      <section className="dd-report-empty">
        <h1>{title}</h1>
        <p>{desc}</p>
        <Link to="/dump-detective">{t('detectiveReport.backToDetect')}</Link>
      </section>
    </main>
  );
}

function DumpDetectiveReportPage() {
  const { t } = useLang();
  const { chain = 'bsc', address = '' } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const normalizedAddress = useMemo(() => address.toLowerCase(), [address]);
  const validRequest = chain === 'bsc' && isValidEvmAddress(normalizedAddress);

  useEffect(() => {
    if (!validRequest) {
      setLoading(false);
      setError(t('detectiveReport.errorBscOnly'));
      return;
    }

    let mounted = true;
    setLoading(true);
    setError('');

    fetch('/api/analyze', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ chain, address: normalizedAddress }),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || t('detectiveReport.errorApiFailed'));
        }
        return data;
      })
      .then((data) => {
        if (!mounted) return;
        setResult(data);
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err.message);
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [chain, normalizedAddress, validRequest, t]);

  if (loading) {
    return <ReportState title={t('detectiveReport.generatingReport')} desc={t('detectiveReport.generatingReportDesc')} />;
  }

  if (error || !result) {
    return <ReportState title={t('detectiveReport.cannotGenerateReport')} desc={error || t('detectiveReport.reportDataEmpty')} />;
  }

  return (
    <main className="dd-report-page">
      <header className="dd-token-header">
        <div>
          <p>Dump Detective · DexScreener Report</p>
          <h1>{result.token.name} <span>{result.token.symbol}</span></h1>
          <div className="dd-address-line">
            <span>BSC</span>
            <code>{shortenAddress(result.address)}</code>
            <span>{new Date().toLocaleString()}</span>
          </div>
        </div>
        <div className="dd-market-grid">
          <span>{t('detectiveReport.price')} <strong>{formatUsd(result.token.priceUsd)}</strong></span>
          <span>{t('detectiveReport.change24h')} <strong className={result.token.priceChange24h >= 0 ? 'up' : 'down'}>{formatPercent(result.token.priceChange24h)}</strong></span>
          <span>{t('detectiveReport.liquidity')} <strong>{formatUsd(result.token.liquidityUsd)}</strong></span>
          <span>{t('detectiveReport.volume24h')} <strong>{formatUsd(result.token.volume24h)}</strong></span>
        </div>
      </header>

      <div className="dd-report-layout">
        <SafetyGauge score={result.scores.total} level={result.riskLevel} />
        <RiskRadar scores={result.scores} />
      </div>

      <ScoreGrid scores={result.scores} />

      <section className="dd-report-panel dd-warning-list">
        <h2>{t('detectiveReport.riskWarnings')}</h2>
        {result.warnings.map((warning) => (
          <article key={warning.title} className={`dd-warning ${warning.level}`}>
            <strong>{warning.title}</strong>
            <p>{warning.desc}</p>
          </article>
        ))}
      </section>

      <div className="dd-report-layout">
        <DetectionDetails result={result} />
        <section className="dd-report-panel dd-summary">
          <h2>{t('detectiveReport.ruleSummary')}</h2>
          <p>{result.summary}</p>
          <small>{t('detectiveReport.summaryDisclaimer')}</small>
        </section>
      </div>

      <ToolboxReturnPanel
        className="dd-report-panel dd-report-toolbox-return"
        description={t('detectiveReport.toolboxDescription')}
        links={[
          { label: t('detectiveReport.patternMatch'), href: '/pattern-match' },
          { label: t('detectiveReport.supportResistance'), href: '/support-resistance' },
        ]}
      />
    </main>
  );
}

export default DumpDetectiveReportPage;
